import React from 'react'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { InferType } from 'yup'
import { Modal } from '../../../../../common/Modal'
import { ModalHeader } from '../../../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../../../hooks/useAppTranslation'
import { ToastType } from '../../../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../../../hooks/useToastNotification'
import { CreateUpdateQuestionsAndAnswersItemForm } from '../CreateUpdateQuestionsAndAnswersItemForm'
// eslint-disable-next-line max-len
import { societyWebsiteFAQQuestionCreateSchema } from '../../../../../../forms/schemas/society_website_faq_question_create'
import { NFaqs } from '../../../../../../interfaces/services/faqs.interfaces'
import { useCurrentSociety } from '../../../../../../hooks/useCurrentSociety'
import { createFaq, patchFaq } from '../../../../../../api/faqs'

interface CreateUpdateQuestionsAndAnswersItemModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  questionId?: string
  sectionId?: string
}

export const CreateUpdateQuestionsAndAnswersItemModal = ({
  questionId,
  sectionId,
  children,
  show,
  close,
}: CreateUpdateQuestionsAndAnswersItemModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!questionId

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateQuestionFailure'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createQuestionFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateQuestionSuccess'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createQuestionSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutatePatchFaqQuestion, isPending: isPatchingFaq } =
    useMutation({
      mutationFn: patchFaq,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })
  const { mutate: mutateCreateFaqQuestion, isPending: isCreatingFaq } =
    useMutation({
      mutationFn: createFaq,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })
  const onSubmit = async (
    data: InferType<typeof societyWebsiteFAQQuestionCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchFaqQuestion({ body: data, id: questionId })
    } else {
      mutateCreateFaqQuestion({
        ...data,
        societyId: society?._id,
      } as NFaqs.NCreate.IRequestBody)
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isPatchingFaq || isCreatingFaq

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateQuestionsAndAnswers.editQuestionTitle'
                  : 'createUpdateQuestionsAndAnswers.addNewQuestionTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateQuestionsAndAnswersItemForm
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                sectionId={sectionId}
                questionId={questionId}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
